/* eslint-disable max-lines */
import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useV2ControlOverrides } from '@bingads-webui-campaign-react/pmax-fluentui-v2-style-overrides';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@adsui/ui-blocker';
import {
  useLogEnteredPage, SIGNUP_FLOW,
} from '@bingads-webui-campaign-react/shared-app-capi';
import { TileHeader } from '@bingads-webui-campaign-react/goals-and-website-tile';
import { Button, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { PerfMarkerFactory } from '@bingads-webui/performance-marker';
import { PageReadyTrigger } from '@bingads-webui-campaign-react/page-ready-trigger';
import { PageReady } from '@bingads-webui/page-ready';
import { omniturePageLoad } from '@bingads-webui-campaign-react/wizard-utils';
import {
  stepToPage, getSubFlowFromCurrentFlow, PMAX_LITE_WIZARD_STEP_PATHS, CAMPAIGN_TYPE,
} from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import { useConfirmDialog } from '@bingads-webui-campaign-react/common-hooks';
import { getUrlWithProtocol } from '@bingads-webui-campaign-react/url-utils';

import { getBusinessInfoStepStyles } from './collect-business-info-styles';
import { useCollectBusinessInfoData } from './use-collect-business-info-data';
import { useReserveTailSpace } from './use-reserve-tail-space';
import { useAlignTailToMain } from './use-align-tail-to-main';
import { useEarlyShellWebsiteInput } from './use-early-shell-website-input';
import { useEarlyShellHandoff, useEarlyShellPerfLogging } from './use-early-shell-handoff';
import { TermsAndConditions } from './components/terms-and-conditions';
import { BusinessInfoShimmer } from './components/business-info-shimmer';
import { BusinessInfoTable } from './components/business-info-table';
import { WebsiteInput } from './components/website-input';
import { PoliticalAdsDeclaration } from './components/political-ads-declaration';
import { isBusinessInfoValid } from './utils';

export const PmaxCollectBusinessInfoIsolatedStep = ({
  onStepSelection,
  supportComponents,
  currentFlow,
  initialWebsiteUrl,
  loadingAtBeginningEnabled,
  isTargetingInsideChina,
  setIsTargetingInsideChina,
  className,
}) => {
  const classes = useDefaultStyles(getBusinessInfoStepStyles);
  const v2Overrides = useV2ControlOverrides();
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const assetGroupId = _.get(scope, ['values', 'assetGroupId']);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const debouncedTriggerLoadBISuggestions = useRef();

  const isPMaxChinaFlowEnabled = usePilotsValue('isPMaxChinaFlowEnabled');
  // autoLog:false — traffic-split exposure must be logged at CAMPAIGN_CREATION_METHOD_SELECTION, not here
  const { value: isEnglishAgenticSignupPilot } = usePilotsValue('isPMaxLiteAgenticSignupEnabled', false, true /* includeNewAccountFlights */, false /* autoLog */);
  const { value: isNonEnglishAgenticSignupPilot } = usePilotsValue('isPMaxLiteAgenticSignupNonEnglishEnabled', false, true /* includeNewAccountFlights */, false /* autoLog */);
  const serverContext = useContext(ServerContext);
  const currentLocale = (serverContext && serverContext.CurrentLocale) || '';
  const isEnglishLocale = currentLocale.toLowerCase().split('-')[0] === 'en';
  const isAgenticSignup = isEnglishLocale
    ? isEnglishAgenticSignupPilot
    : isNonEnglishAgenticSignupPilot;

  const isEarlyShellEnabled = !!window.pmaxLiteEarlyShellEnabled;

  // earlyShellActive: true while #static-early-shell is in the DOM.
  // Flipped false directly by onStepSelectionWithShellHide on real navigation.
  const [earlyShellActive, setEarlyShellActive] = useState(isEarlyShellEnabled);

  // Performs the handoff (hides static placeholders, reveals #root) once on mount.
  useEarlyShellHandoff(isEarlyShellEnabled);

  // Purely React-internal: whether React's own <BusinessInfoShimmer> has taken over from the
  // static #es-shimmer-temp. Real state, not a ref — see the render-time flip further down,
  // right after isLoadingBI/loadComplete are available. Never observed outside React, so it
  // stays local here instead of on a shared global.
  const [isReactShimmerActive, setIsReactShimmerActive] = useState(false);

  const campaignCreationMethodStepId = PMAX_LITE_WIZARD_STEP_PATHS.CAMPAIGN_CREATION_METHOD_SELECTION;

  const instrumentation = useContext(InstrumentationContext);
  const activity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.collectBusinessInfoStep.create();
    }
    return { trace: _.noop, error: _.noop, perf: _.noop };
  }, [instrumentation]);
  // Reports the pmax_early_shell_* perf marks through instrumentation — split out from the
  // handoff itself since logging isn't time-critical and shouldn't hold up its paint.
  useEarlyShellPerfLogging(isEarlyShellEnabled, activity);

  const {
    inlineSupport: InlineSupport,
  } = supportComponents;

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCache, setIsLoadingCache] = useState(false);
  const [dataValid, setDataValid] = useState({});
  const [triggerLoadingBI, setTriggerLoadingBI] = useState(false);
  const [previousValidWebsite, setPreviousValidWebsite] = useState('');
  const [saveFailed, setSaveFailed] = useState(false);

  const businessInfoStepMarkerRef = useRef(null);
  if (businessInfoStepMarkerRef.current === null) {
    businessInfoStepMarkerRef.current = PerfMarkerFactory.createMarker('PMaxLiteWizard.COLLECT_BUSINESS_INFO');
    businessInfoStepMarkerRef.current.willInit();
  }
  const businessInfoStepMarker = businessInfoStepMarkerRef.current;

  const {
    isLoading,
    bizInfoData = {},
    websiteUrlPreference,
    updateBizInfoData,
    loadBISuggestionsAndData,
    save,
  } = useCollectBusinessInfoData(activity);

  const {
    countryCode,
    websiteUrl,
    terms = {},
    isLoadingBI,
    loadComplete,
    noNeedLoadSuggesion,
  } = bizInfoData || {};

  // Flip isReactShimmerActive during render (call setState directly in the render body) instead
  // of in an effect, so loadComplete-becomes-true and isReactShimmerActive-becomes-true always
  // land in the same render — no frame where one shimmer is gone but the other isn't gated open
  // yet. Safe without tracking a "previous loadComplete" value: isReactShimmerActive only ever
  // goes false→true once and is never reset, so !isReactShimmerActive alone already prevents
  // this from firing again on any later render.
  if (!isLoadingBI && loadComplete && !isReactShimmerActive) {
    setIsReactShimmerActive(true);
  }

  const onConfirmNext = useCallback(() => {
    setPreviousValidWebsite(websiteUrl);
    setTriggerLoadingBI(true);
    setSaveFailed(false);
    updateBizInfoData({
      triggerDeleteCampaign: true,
    });
  }, [updateBizInfoData, websiteUrl]);

  // When early shell is active and the user navigates away from step 1, remove the static
  // shell directly — interactive.ts no longer has any listener for this. Also flip the enabled
  // flag off: if the user comes back to step 1 later (e.g. back navigation remounts this
  // component), #static-early-shell is already gone, so isEarlyShellEnabled must read false on
  // that next mount or earlyShellActive would initialize true with no shell left to portal into.
  const onStepSelectionWithShellHide = useCallback((...args) => {
    if (earlyShellActive) {
      const shellEl = document.getElementById('static-early-shell');
      if (shellEl && shellEl.parentNode) { shellEl.parentNode.removeChild(shellEl); }
      window.pmaxLiteEarlyShellEnabled = false;
      setEarlyShellActive(false);
    }
    onStepSelection(...args);
  }, [earlyShellActive, onStepSelection]);

  const onAbortNext = useCallback(() => {
    updateBizInfoData({
      triggerResumeBizInfo: true,
    });
  }, [updateBizInfoData]);

  const [
    changeDialog,
    showChangeDialog,
    isChangeDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('Confirm changes')),
    content: i18n.getString(_TL_('When you confirm a change to the website, the associated campaign will be deleted.')),
    confirmText: i18n.getString(_TL_('Confirm')),
    onConfirm: onConfirmNext,
    cancelText: i18n.getString(_TL_('Cancel')),
    onCancel: onAbortNext,
    className: 'wizard-change-website-dialog',
  }, {
    dialogName: 'WizardChangeWebsiteDialog',
  });

  const triggerLoadBISuggestions = useCallback((url) => {
    if (previousValidWebsite !== url) {
      if (assetGroupId) {
        showChangeDialog();
      } else {
        setPreviousValidWebsite(url);
        setTriggerLoadingBI(true);
        setSaveFailed(false);
      }
    }
  }, [assetGroupId, previousValidWebsite, showChangeDialog]);

  const onWebsiteValidate = useCallback((e) => {
    const newWebsiteUrl = _.get(e, ['data', 'website'], '');
    setDataValid(prevState => ({
      ...prevState,
      isWebsiteValid: e.isValid,
    }));
    if (e.isValid) {
      if (noNeedLoadSuggesion) {
        setPreviousValidWebsite(newWebsiteUrl);
        updateBizInfoData({
          noNeedLoadSuggesion: false,
        });
      } else {
        debouncedTriggerLoadBISuggestions.current = _.debounce(triggerLoadBISuggestions, 700);
        debouncedTriggerLoadBISuggestions.current(newWebsiteUrl);
      }
    }
  }, [noNeedLoadSuggesion, triggerLoadBISuggestions, updateBizInfoData]);

  const onWebsiteChange = useCallback((value) => {
    updateBizInfoData({
      websiteUrl: value,
    });
    // Cancel the previous debounced function if website change
    if (debouncedTriggerLoadBISuggestions.current) {
      debouncedTriggerLoadBISuggestions.current.cancel();
    }
  }, [updateBizInfoData]);

  const loadBISuggestionAndData = useCallback(
    (url) => {
      setTriggerLoadingBI(false);
      loadBISuggestionsAndData(url);
    },
    [loadBISuggestionsAndData]
  );

  const onNextClick = useCallback(() => {
    if (isSaving) return;

    const isValid = _.isEmpty(dataValid) || _.every(dataValid, Boolean);
    if (!isValid) {
      activity.trace(`[CollectBusinessInfo] Validation failed, cannot proceed to next step: ${JSON.stringify(dataValid)}`);
      setSaveFailed(true);
      return;
    }

    setIsSaving(true);
    save()
      .then(() => {
        if (isPMaxChinaFlowEnabled && isTargetingInsideChina) {
          activity.trace('[ChinaTargeting] User selected targeting inside China');
          onStepSelectionWithShellHide(PMAX_LITE_WIZARD_STEP_PATHS.CHINA_DOMESTIC, {
            isCollectBusinessInfo: true,
            isTargetingInsideChina: true,
          });
          return;
        }
        if (isPMaxChinaFlowEnabled && countryCode === 'CN' && !isTargetingInsideChina) {
          activity.trace('[ChinaTargeting] User selected targeting outside China');
        }
        const urlWithProtocol = getUrlWithProtocol(websiteUrl);
        if (initialWebsiteUrl) {
          // remove cacheId from scope when changed website
          if (initialWebsiteUrl !== websiteUrl) {
            scope.remove(['cacheId', 'email', 'signature']);
            scope.save(undefined, undefined, { replace: true });
            // go to next step
            const nextStepId = campaignCreationMethodStepId;
            activity.trace(`[CollectBusinessInfo] Navigating to next step: ${nextStepId}`);
            onStepSelectionWithShellHide(
              nextStepId,
              { isCollectBusinessInfo: true, isGoalBasedSignup: true, isAgenticSignup },
              { websiteUrl: urlWithProtocol, websites: [urlWithProtocol] }
            );
          } else {
            // if use cache url, go to preview step directly
            onStepSelectionWithShellHide(
              PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ASSETS,
              {
                isCollectBusinessInfo: true,
                selectedCampaignType: CAMPAIGN_TYPE.PMAX,
                isGoalBasedSignup: true,
                isAgenticSignup,
                isPreviewFlow: true,
              },
              { isShowingAnimation: true, websiteUrl: urlWithProtocol, websites: [urlWithProtocol] }
            );
          }
        } else {
          // go to next step
          const nextStepId = campaignCreationMethodStepId;
          activity.trace(`[CollectBusinessInfo] Navigating to next step: ${nextStepId}`);
          onStepSelectionWithShellHide(
            nextStepId,
            { isCollectBusinessInfo: true, isGoalBasedSignup: true, isAgenticSignup },
            { websiteUrl: urlWithProtocol, websites: [urlWithProtocol] }
          );
        }
      })
      .catch((e) => {
        activity.trace('[CollectBusinessInfo] Error occurred while saving', JSON.stringify(e));
        setSaveFailed(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [activity, campaignCreationMethodStepId, countryCode, dataValid, initialWebsiteUrl,
    isAgenticSignup, isSaving, isPMaxChinaFlowEnabled,
    isTargetingInsideChina, onStepSelectionWithShellHide, save, scope, websiteUrl]);

  useEffect(() => {
    _.delay(() => {
      PageReady.pageIsReady();
    }, 15000);
  }, []);

  useEffect(() => {
    if (businessInfoStepMarker) {
      businessInfoStepMarker.done();
    }
  }, [businessInfoStepMarker]);

  const currentStepId = PMAX_LITE_WIZARD_STEP_PATHS.COLLECT_BUSINESS_INFO;
  const flow = SIGNUP_FLOW.PmaxLiteSignup;
  const subFlow = getSubFlowFromCurrentFlow(currentFlow);

  useLogEnteredPage({ Flow: flow, SubFlow: subFlow, Page: stepToPage[currentStepId] });

  useEffect(() => {
    omniturePageLoad({
      isSignUp: true,
      pageId: stepToPage[currentStepId] || 'unknown step',
      pageTitle: currentStepId,
      signUpPrefix: 'pmax_lite_signup_step_',
    });
  }, [currentStepId]);

  useEffect(() => {
    if (dataValid && dataValid.isWebsiteValid && triggerLoadingBI && !isLoadingBI) {
      loadBISuggestionAndData(websiteUrl);
    }
  }, [websiteUrl, dataValid, loadBISuggestionAndData, isLoadingBI, triggerLoadingBI]);

  // for cache flow
  useEffect(() => {
    if (loadingAtBeginningEnabled && initialWebsiteUrl) {
      setIsLoadingCache(true);
    }
  }, [initialWebsiteUrl, loadingAtBeginningEnabled]);

  useEffect(() => {
    if (isLoadingCache) {
      if (!_.isEmpty(bizInfoData.supportedCountryList) && !websiteUrlPreference) {
        updateBizInfoData({
          websiteUrl: initialWebsiteUrl,
        });
        setIsLoadingCache(false);
      }

      if (loadComplete) {
        setIsLoadingCache(false);
      }
    }
  }, [bizInfoData.supportedCountryList, initialWebsiteUrl,
    isLoadingCache, loadComplete, updateBizInfoData, websiteUrlPreference]);

  useEarlyShellWebsiteInput(isEarlyShellEnabled, onWebsiteChange, onWebsiteValidate, i18n);

  // Diagnostic: the "Next" button can get stuck disabled on resume for hard-to-reproduce data
  // states (see the disabled predicate on the button below). Because onNextClick (and its traces)
  // never runs while the button is disabled, emit a one-time breakdown of the disable predicate
  // once the page has settled, so the exact failing gate is captured in telemetry. Sensitive
  // fields (email/phone/businessName) are logged as booleans only.
  const nextDisabledLoggedRef = useRef(false);
  useEffect(() => {
    const dataValidPass = _.isEmpty(dataValid) || _.every(dataValid, Boolean);
    const bizInfoValid = isBusinessInfoValid(bizInfoData);
    const isNextDisabled = isSaving || isLoading || isLoadingBI || isLoadingCache
      || !dataValidPass || !bizInfoValid;
    // Only inspect the "stuck" state: page settled (nothing loading) but Next still disabled.
    const pageSettled = !isSaving && !isLoading && !isLoadingBI && !isLoadingCache && loadComplete;
    if (isNextDisabled && pageSettled && !nextDisabledLoggedRef.current) {
      nextDisabledLoggedRef.current = true;
      activity.trace(`[CollectBusinessInfo] Next stuck disabled after load: ${JSON.stringify({
        dataValid,
        dataValidPass,
        bizInfoValid,
        fields: {
          email: !!bizInfoData.email,
          phoneNumber: !!bizInfoData.phoneNumber,
          callingCode: bizInfoData.callingCode,
          callingCountryCode: bizInfoData.callingCountryCode,
          countryCode: bizInfoData.countryCode,
          businessName: !!bizInfoData.businessName,
          websiteUrl: !!bizInfoData.websiteUrl,
          termsAccepted: !!terms.accepted,
          shouldDisableChecked: !!terms.shouldDisableChecked,
          isPoliticalAdsConfirmed: !!bizInfoData.isPoliticalAdsConfirmed,
        },
      })}`);
    } else if (!pageSettled) {
      // Re-arm the one-time guard if the page returns to a loading state, so a later stuck state re-logs.
      nextDisabledLoggedRef.current = false;
    }
  }, [activity, bizInfoData, dataValid, isSaving, isLoading, isLoadingBI, isLoadingCache,
    loadComplete, terms]);

  // Part 1: website URL input — static/always-live per the early-shell design. Rendered here
  // only for the normal (non-early-shell) flow; the early-shell flow's Part 1 is the static
  // #es-website-input painted at FCP, never portaled.
  const part1Content = (
    <WebsiteInput
      websiteUrl={websiteUrl || ''}
      onWebsiteChange={onWebsiteChange}
      onWebsiteValidate={onWebsiteValidate}
      alwaysShowError={saveFailed}
    />
  );

  // Part 2: BI shimmer -> BI table -> T&C -> political ads declaration — portaled into
  // #es-react-slot for the early-shell flow.
  const part2Content = (
    <>
      {isLoadingBI && (!earlyShellActive || isReactShimmerActive) && (
        <BusinessInfoShimmer />
      )}

      {!isLoadingBI && loadComplete && (
        <React.Fragment>
          <div>
            <BusinessInfoTable
              bizInfoData={bizInfoData}
              updateBizInfoData={updateBizInfoData}
              setDataValid={setDataValid}
              alwaysShowError={saveFailed}
              activity={activity}
              isTargetingInsideChina={isTargetingInsideChina}
              setIsTargetingInsideChina={setIsTargetingInsideChina}
            />
          </div>

          <TermsAndConditions
            terms={terms}
            countryCode={countryCode}
            updateBizInfoData={updateBizInfoData}
            showValidationResult={saveFailed}
          />

          <PoliticalAdsDeclaration
            bizInfoData={bizInfoData}
            updateBizInfoData={updateBizInfoData}
            activity={activity}
            isSaving={isSaving}
            showError={saveFailed}
          />
        </React.Fragment>
      )}
    </>
  );

  const businessInfoContent = (
    <div className={classNames('card-content-container', classes.businessInfoContainer)}>
      {!earlyShellActive && part1Content}
      {part2Content}
    </div>
  );

  const footerContent = (
    <div className={classNames('footer-container', classes.footerContainer)}>
      <Button
        appearance="primary"
        size="large"
        onClick={onNextClick}
        className="step-next-button"
        disabled={isSaving || isLoading || isLoadingBI || isLoadingCache
          || !(_.isEmpty(dataValid) || _.every(dataValid, Boolean))
          || !isBusinessInfoValid(bizInfoData)}
      >
        {i18n.getString(_TL_('Next'))}
      </Button>
    </div>
  );

  const part2SlotEl = earlyShellActive ? document.getElementById('es-react-slot') : null;
  const footerSlotEl = earlyShellActive ? document.getElementById('es-footer-slot') : null;
  const inlineSupportRef = useRef(null);
  useReserveTailSpace(earlyShellActive, 'static-early-shell', inlineSupportRef); useAlignTailToMain(earlyShellActive, inlineSupportRef);
  useLayoutEffect(() => {
    if (!earlyShellActive) return;
    // Hide the static disabled Next button once React portals the functional one in
    const staticBtn = document.getElementById('es-next-button-temp'); if (footerSlotEl && staticBtn) staticBtn.style.display = 'none';
  }, [earlyShellActive, footerSlotEl]);
  // isReactShimmerActive flips true during render (above), so by the time this effect ever sees
  // it true, part2 content is already showing — this just tears the static shimmer down for
  // good and leaves the slot permanently visible. Before that: typing a valid-looking URL before
  // React mounts makes interactive.ts's pre-mount listener add 'es-loading' on its own, and on
  // mount isLoadingBI is still undefined (fetch not started, waiting on the validate debounce) —
  // so this must not reflexively clear 'es-loading' just because isLoadingBI is falsy, or it'd
  // race ahead of the real load and blank the slot. Only isLoadingBI:true (add) or
  // dataValid.isWebsiteValid===false (React's real validation disagreeing with the pre-mount
  // guess — remove) may touch the class pre-handoff.
  useLayoutEffect(() => {
    if (!earlyShellActive || !part2SlotEl) return;
    if (isReactShimmerActive) {
      const shimmer = document.getElementById('es-shimmer-temp');
      if (shimmer && shimmer.parentNode) { shimmer.parentNode.removeChild(shimmer); }
      part2SlotEl.classList.add('visible');
    } else if (isLoadingBI) {
      part2SlotEl.classList.add('es-loading');
    } else if (dataValid.isWebsiteValid === false) {
      part2SlotEl.classList.remove('es-loading');
    }
  }, [earlyShellActive, isLoadingBI, isReactShimmerActive, part2SlotEl, dataValid.isWebsiteValid]);

  if (earlyShellActive) {
    return (
      <>
        {isChangeDialogVisible && changeDialog}
        <div className={classNames('card-layout', classes.cardLayout, classes.cardLayoutEarlyShell, v2Overrides.root, className)}>
          <div className={classNames('tail-container', classes.tailContainerFixed)} ref={inlineSupportRef}>
            <div className={classes.tailContainerInner}>
              <InlineSupport />
            </div>
          </div>
        </div>
        {/* v2Overrides.root is re-applied here: it's only a class-scoped (descendant-selector)
            CSS override for legacy Fabric v1 controls (e.g. the Location/calling-code
            SearchableDropdown's .ms-Dropdown-title), and createPortal moves this subtree's DOM
            outside card-layout above — its own v2Overrides.root wrap doesn't reach here. Without
            it, those v1 controls fall back to their raw un-themed border, breaking the "outline
            with only the bottom edge dark" look the rest of the form has. */}
        {part2SlotEl && createPortal(
          <FluentProvider theme={webLightTheme} className={v2Overrides.root} style={{ display: 'contents' }}>
            {businessInfoContent}
            <PageReadyTrigger />
          </FluentProvider>,
          part2SlotEl
        )}
        {footerSlotEl && createPortal(
          <FluentProvider theme={webLightTheme} className={v2Overrides.root} style={{ display: 'contents' }}>
            {footerContent}
          </FluentProvider>,
          footerSlotEl
        )}
        {isSaving && createPortal(
          // Portaled straight to <body> (not rendered in place): position:fixed only escapes
          // normal document flow, not an ancestor's stacking context — if anything between
          // #root and this component establishes one (transform/filter/z-index wrapper, common
          // in the real app shell but absent in the isolated demo), a z-index set here would be
          // trapped inside it and could still lose to #static-early-shell's body-level z-index
          // (10). Rendering at the body level like #static-early-shell itself sidesteps that.
          <div className={classes.earlyShellSavingOverlay}>
            <FluentProvider theme={webLightTheme} style={{ display: 'contents' }}>
              <UiBlocker blocking loadingText={i18n.getString(_TL_('Saving...'))} />
            </FluentProvider>
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div className={classNames('card-layout', classes.cardLayout, v2Overrides.root, className)}>
      <UiBlocker blocking={isSaving || (!earlyShellActive && (isLoading || isLoadingCache))} loadingText={isSaving ? i18n.getString(_TL_('Saving...')) : i18n.getString(_TL_('Loading...'))}>
        <div className={classNames('main-container', classes.mainContainer, classes[`mainContainer-${displayMode}`])} role="main">
          {!earlyShellActive && (
            <TileHeader
              title={i18n.getString(_TL_('Tell us about your business to get started'))}
              displayMode={displayMode}
              hideIcon
            />
          )}
          <div className={classNames('card-container', classes.cardContainer, classes[`cardContainer-${displayMode}`])}>
            <div className={classNames('card-content-container', classes.cardContentContainer, classes[`cardContentContainer-${displayMode}`])}>
              {businessInfoContent}
              <PageReadyTrigger />
            </div>
          </div>
          {footerContent}
        </div>
        <div className={classNames('tail-container', classes.tailContainer)}>
          <InlineSupport />
        </div>
        {isChangeDialogVisible && changeDialog}
      </UiBlocker>
    </div>
  );
};

PmaxCollectBusinessInfoIsolatedStep.propTypes = {
  onStepSelection: PropTypes.func.isRequired,
  supportComponents: PropTypes.shape({
    inlineSupport: PropTypes.func.isRequired,
  }).isRequired,
  currentFlow: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialWebsiteUrl: PropTypes.string,
  loadingAtBeginningEnabled: PropTypes.bool,
  isTargetingInsideChina: PropTypes.bool,
  setIsTargetingInsideChina: PropTypes.func.isRequired,
  className: PropTypes.string,
};

PmaxCollectBusinessInfoIsolatedStep.defaultProps = {
  initialWebsiteUrl: '',
  loadingAtBeginningEnabled: false,
  isTargetingInsideChina: false,
  className: undefined,
};
